import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

import type { OverlayCandidate, ScreenOverlay } from '../types'
import { GoldenRain } from './festive'
import { CandidatePhoto, ClampToFit } from './shared'

/**
 * Ce que le MC pose par-dessus l'écran : les profils des candidats (pour le
 * pronostic de la salle), ou l'annonce d'un gagnant.
 *
 * Plein écran, sans l'horloge ni le programme : pendant ces quelques minutes,
 * la télé est un décor de scène, comme pour le gâteau.
 */
export function OverlayView({ overlay }: { overlay: ScreenOverlay }) {
  return overlay.kind === 'candidates' ? (
    <CandidatesOverlay overlay={overlay} />
  ) : overlay.kind === 'quiz' ? (
    <QuizOverlay overlay={overlay} />
  ) : (
    <AnnouncementOverlay overlay={overlay} />
  )
}

/**
 * La taille d'un texte du quiz selon sa longueur : une question courte se lit
 * en très grand, une longue descend d'un cran plutôt que de déborder. Les
 * paliers tiennent la limite du serveur (300 caractères la question, 160 la
 * réponse) sur 1920×1080 comme sur 1280×720, sans défilement.
 */
function quizQuestionSize(length: number): string {
  if (length <= 70) return 'min(8vh, 4.6vw)'
  if (length <= 140) return 'min(6.4vh, 3.7vw)'
  if (length <= 220) return 'min(5.2vh, 3vw)'
  return 'min(4.4vh, 2.6vw)'
}

function quizAnswerSize(longest: number): string {
  if (longest <= 28) return 'min(6vh, 3.4vw)'
  if (longest <= 60) return 'min(4.8vh, 2.8vw)'
  if (longest <= 110) return 'min(3.8vh, 2.2vw)'
  return 'min(3.2vh, 1.85vw)'
}

/**
 * Une question du quiz, lue du fond de la salle : la question en très grand,
 * puis quatre cases A, B, C, D en grille 2 × 2.
 *
 * À la révélation, la bonne case s'allume (or, coche) et les autres
 * s'éteignent — sans refaire l'entrée de la page : la salle garde les yeux
 * sur la même grille. Moins d'animations demandé : le changement est net,
 * sans halo qui pulse.
 */
function QuizOverlay({ overlay }: { overlay: Extract<ScreenOverlay, { kind: 'quiz' }> }) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const revealed = overlay.revealed_letter
  const longest = Math.max(1, ...overlay.answers.map((answer) => answer.text.length))
  const answerSize = quizAnswerSize(longest)

  return (
    <div className="flex h-full min-h-0 flex-col gap-[3vh]">
      <header className="flex shrink-0 items-baseline justify-between gap-[2vw]">
        <p className="min-w-0 truncate pb-[0.4vh] font-display text-[min(4.4vh,2.6vw)] leading-tight text-champagne-300">
          {overlay.title}
        </p>
        <p className="shrink-0 font-mono text-[min(4.4vh,2.6vw)] font-semibold tabular-nums leading-none text-content-primary">
          {t('screen.quiz.number', { number: overlay.number, total: overlay.total })}
        </p>
      </header>

      <h1
        className="shrink-0 break-words text-center font-display leading-[1.12] text-content-primary"
        style={{ fontSize: quizQuestionSize(overlay.question.length) }}
      >
        {overlay.question}
      </h1>

      <ol className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-[2.2vh_1.8vw]">
        {overlay.answers.map((answer) => {
          const correct = revealed === answer.letter
          const dimmed = revealed !== null && !correct
          return (
            <motion.li
              key={answer.letter}
              className={cn(
                'relative flex min-h-0 min-w-0 items-center gap-[2vw] overflow-hidden rounded-2xl border-[0.35vh] px-[2vw] py-[2vh]',
                correct
                  ? 'border-champagne-300 bg-champagne-400/20'
                  : 'border-champagne-400/40 bg-bg-elevated',
              )}
              // Le halo est posé en style, pas animé : une ombre en `color-mix`
              // ne s'interpole pas, elle sauterait de toute façon.
              style={
                correct
                  ? { boxShadow: '0 0 6vh color-mix(in srgb, var(--champagne-400) 55%, transparent)' }
                  : {}
              }
              animate={{
                opacity: dimmed ? 0.28 : 1,
                scale: correct && !reduce ? 1.025 : 1,
              }}
              transition={{ duration: reduce ? 0 : 0.6, ease: 'easeOut' }}
            >
              <span
                className={cn(
                  'flex aspect-square h-[min(13vh,7.5vw)] shrink-0 items-center justify-center rounded-full font-display font-semibold leading-none',
                  correct
                    ? 'bg-champagne-300 text-bg-base'
                    : 'border-[0.35vh] border-champagne-400/70 text-champagne-300',
                )}
                style={{ fontSize: 'min(8vh, 4.6vw)' }}
              >
                {answer.letter}
              </span>
              <span
                className="min-w-0 flex-1 break-words leading-[1.15] text-content-primary"
                style={{ fontSize: answerSize }}
              >
                {answer.text}
              </span>
              {correct ? (
                <span
                  className="shrink-0 font-semibold leading-none text-champagne-300"
                  style={{ fontSize: 'min(9vh, 5vw)' }}
                  role="img"
                  aria-label={t('screen.quiz.correct')}
                >
                  ✓
                </span>
              ) : null}
            </motion.li>
          )
        })}
      </ol>
    </div>
  )
}

/**
 * Combien de profils un concours montre à la fois. Jusqu'à huit, tout tient
 * sur la page (une ligne par candidat quand Miss et Master sont côte à côte) ;
 * au-delà, les lignes deviendraient illisibles à dix mètres : le concours se
 * lit en plusieurs pages, qui tournent seules.
 */
const PROFILES_PER_PAGE = 8
/** Le temps de lire une page de profils du fond de la salle. */
const PAGE_SECONDS = 12

/** La place des cartes, en unités d'écran, une fois titre et en-têtes posés. */
const GRID_HEIGHT_VH = 79
const GRID_WIDTH_VW = 94
const COLUMN_GAP_VW = 2.5
const CARD_GAP_VH = 1.4

interface ProfileLayout {
  cols: number
  rows: number
  cardHeightVh: number
  nameSize: string
  bodySize: string
  padding: string
  gap: string
  /**
   * Une carte assez haute laisse la devise (ou âge · activité · études)
   * passer à la ligne ; une carte basse les coupe d'un « … » plutôt que
   * de prendre la ligne de la présentation.
   */
  wrap: boolean
}

/**
 * La disposition des profils, du plus lisible au plus serré.
 *
 * Toujours des cartes larges et basses : une ligne de texte longue se lit
 * mieux qu'une carte étroite où la présentation n'aurait plus que trois mots
 * par ligne. Miss et Master côte à côte : une colonne de cartes par concours.
 * Un concours seul prend toute la largeur, sur deux colonnes. Toutes les
 * tailles découlent de la hauteur de la carte : six candidats ou deux, la
 * page tient sans défiler.
 */
function profileLayout(count: number, categories: number): ProfileLayout {
  const columnWidth = (GRID_WIDTH_VW - COLUMN_GAP_VW * (categories - 1)) / Math.max(categories, 1)
  const cols = categories > 1 || count <= 1 ? 1 : 2
  const rows = Math.max(1, Math.ceil(count / cols))
  const cardWidth = (columnWidth - 1.2 * (cols - 1)) / cols
  const cardHeight = (GRID_HEIGHT_VH - CARD_GAP_VH * (rows - 1)) / rows
  const name = Math.min(cardHeight * 0.24, 5.5)
  const body = Math.min(Math.max(cardHeight * 0.13, 1.45), 2.4)
  // Peu de marge en hauteur : sur six lignes par concours, chaque pixel
  // rendu à la présentation lui donne une ligne de plus.
  const paddingY = Math.min(cardHeight * 0.05, 1.4)
  const paddingX = Math.min(cardHeight * 0.1, 1.8)
  return {
    cols,
    rows,
    cardHeightVh: cardHeight,
    nameSize: `min(${name.toFixed(2)}vh, ${(cardWidth * 0.075).toFixed(2)}vw)`,
    bodySize: `min(${body.toFixed(2)}vh, ${(cardWidth * 0.05).toFixed(2)}vw)`,
    padding: `${paddingY.toFixed(2)}vh ${paddingX.toFixed(2)}vh`,
    gap: `${Math.min(cardHeight * 0.04, 0.9).toFixed(2)}vh`,
    wrap: cardHeight >= 16,
  }
}

function CandidatesOverlay({
  overlay,
}: {
  overlay: Extract<ScreenOverlay, { kind: 'candidates' }>
}) {
  const reduce = useReducedMotion()
  const categories = useMemo(
    () => overlay.categories.filter((category) => category.candidates.length > 0),
    [overlay.categories],
  )
  const pages = Math.max(
    1,
    ...categories.map((category) => Math.ceil(category.candidates.length / PROFILES_PER_PAGE)),
  )
  const [page, setPage] = useState(0)

  useEffect(() => {
    if (pages <= 1) return
    const timer = window.setInterval(() => setPage((value) => (value + 1) % pages), PAGE_SECONDS * 1000)
    return () => window.clearInterval(timer)
  }, [pages])

  const current = page % pages
  // Sur une page donnée, seuls les concours qui y ont encore des candidats :
  // une colonne vide ressemblerait à une panne.
  const shown = categories
    .map((category) => ({
      ...category,
      candidates: category.candidates.slice(
        current * PROFILES_PER_PAGE,
        (current + 1) * PROFILES_PER_PAGE,
      ),
    }))
    .filter((category) => category.candidates.length > 0)
  // Une même taille de carte pour tous les concours de la page : Miss et
  // Master se lisent pareil, même si l'un a un candidat de moins. Sur une
  // page suivante, la taille reste celle d'une page pleine.
  const densest = Math.max(
    1,
    ...categories.map((category) => Math.min(category.candidates.length, PROFILES_PER_PAGE)),
  )
  const layout = profileLayout(densest, Math.max(shown.length, 1))

  return (
    <div className="flex h-full min-h-0 flex-col gap-[2vh]">
      <header className="flex shrink-0 items-baseline justify-center gap-[2vw]">
        <h1 className="truncate text-center font-display text-[min(5.5vh,3.2vw)] leading-none text-champagne-300">
          {overlay.title}
        </h1>
        {pages > 1 ? (
          <p className="shrink-0 font-mono text-[min(3vh,1.8vw)] tabular-nums text-content-secondary">
            {current + 1} / {pages}
          </p>
        ) : null}
      </header>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={current}
          className="grid min-h-0 flex-1"
          style={{
            gridTemplateColumns: `repeat(${Math.max(shown.length, 1)}, minmax(0, 1fr))`,
            columnGap: `${COLUMN_GAP_VW}vw`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.2 : 0.5 }}
        >
          {shown.map((category) => (
            <section key={category.id} className="flex min-h-0 min-w-0 flex-col gap-[1.2vh]">
              <h2 className="shrink-0 truncate text-center font-display text-[min(3.8vh,2.2vw)] leading-tight text-content-primary">
                {category.name}
              </h2>
              <ul
                className="grid min-h-0 flex-1"
                style={{
                  gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
                  gap: `${CARD_GAP_VH}vh 1.2vw`,
                }}
              >
                {category.candidates.map((candidate) => (
                  <li key={candidate.id} className="flex min-h-0 min-w-0">
                    <ProfileCard candidate={candidate} layout={layout} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/**
 * Le profil public d'un candidat, en carte : de quoi parier sur quelqu'un.
 *
 * Photo et numéro (celui du bulletin), nom, âge et activité, études, les
 * trois mots, la devise — toujours visibles — puis sa présentation, qui
 * prend la place restante et s'arrête sur « … » plutôt que de déborder.
 */
function ProfileCard({ candidate, layout }: { candidate: OverlayCandidate; layout: ProfileLayout }) {
  const { t } = useTranslation()
  const words = candidate.three_words ?? []
  const education = candidate.education_level
    ? t('screen.education', { level: candidate.education_level })
    : null
  const facts = [
    candidate.age !== null && candidate.age !== undefined
      ? t('screen.age', { count: candidate.age })
      : null,
    candidate.activity ?? null,
  ].filter((value): value is string => Boolean(value))
  const motto = candidate.motto ? t('screen.motto', { motto: candidate.motto }) : null
  const details = [...facts, education].filter(Boolean).join(' · ')
  const body = { fontSize: layout.bodySize }
  const wrap = layout.wrap

  return (
    <article className="flex h-full min-h-0 w-full min-w-0 overflow-hidden rounded-lg border border-[color-mix(in_srgb,var(--champagne-400)_40%,transparent)] bg-bg-elevated">
      <div
        className="relative h-full shrink-0 overflow-hidden"
        style={{ aspectRatio: '3 / 4', maxWidth: '30%' }}
      >
        <CandidatePhoto
          url={candidate.photo_url}
          number={candidate.number}
          name={candidate.name}
          className="[&>span]:text-[min(8vh,5vw)]"
        />
        {candidate.number !== null && candidate.photo_url ? (
          <span
            className="absolute left-[0.6vh] top-[0.6vh] rounded-md bg-black/75 px-[0.8vh] py-[0.2vh] font-mono font-semibold leading-tight text-champagne-300"
            style={{ fontSize: `min(${Math.min(layout.cardHeightVh * 0.17, 4).toFixed(2)}vh, 2.4vw)` }}
          >
            {t('screen.number', { number: candidate.number })}
          </span>
        ) : null}
      </div>

      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        style={{ padding: layout.padding, gap: layout.gap }}
      >
        {/* Le nom, puis âge · activité · études sur la même ligne s'il y a
            la place — sinon dessous, plutôt que coupé. */}
        <div className={cn('flex min-w-0 shrink-0 items-baseline gap-x-[1.4vh]', wrap && 'flex-wrap')}>
          <p
            className={cn('truncate font-display leading-[1.1] text-content-primary', wrap ? 'max-w-full' : 'max-w-[60%] shrink-0')}
            style={{ fontSize: layout.nameSize }}
          >
            {candidate.name}
          </p>
          {details ? (
            <p className="min-w-0 max-w-full truncate leading-snug text-content-secondary" style={body}>
              {details}
            </p>
          ) : null}
        </div>
        {/* Les trois mots et la devise : toujours visibles, la présentation
            prend ce qui reste. */}
        {words.length > 0 || motto ? (
          <ul className={cn('flex min-w-0 shrink-0 items-center gap-x-[0.7vh] gap-y-[0.4vh]', wrap && 'flex-wrap')}>
            {words.map((word) => (
              <li
                key={word}
                className="shrink-0 whitespace-nowrap rounded-full border border-[color-mix(in_srgb,var(--champagne-400)_60%,transparent)] px-[1vh] leading-snug text-champagne-300"
                style={body}
              >
                {word}
              </li>
            ))}
            {motto ? (
              <li
                className="ml-[0.6vh] min-w-0 max-w-full truncate font-display italic leading-snug text-content-primary"
                style={body}
              >
                {motto}
              </li>
            ) : null}
          </ul>
        ) : null}
        {candidate.bio ? (
          <ClampToFit
            text={candidate.bio}
            className="leading-snug text-content-secondary"
            style={body}
          />
        ) : null}
      </div>
    </article>
  )
}

/**
 * Le gagnant désigné, en très grand — et une pluie d'or discrète.
 *
 * L'effet se coupe pour qui a demandé moins d'animations (réglage système du
 * navigateur de la télé) : l'annonce reste entière, seule la fête se tait.
 */
function AnnouncementOverlay({
  overlay,
}: {
  overlay: Extract<ScreenOverlay, { kind: 'announcement' }>
}) {
  const reduce = useReducedMotion()

  return (
    <div className="relative flex h-full min-h-0 flex-col items-center justify-center overflow-hidden text-center">
      <GoldenRain seed={overlay.published_at} />
      <div className="relative">
        <p className="font-display text-[min(6vh,3.6vw)] leading-tight text-content-secondary">
          {overlay.title}
        </p>
        <motion.p
          className="mt-[3vh] break-words font-display text-[min(16vh,9vw)] leading-[1.02] text-champagne-300"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
        >
          {overlay.winner}
        </motion.p>
        {overlay.detail ? (
          <p className="mx-auto mt-[3vh] max-w-[70vw] text-[min(4vh,2.4vw)] leading-snug text-content-primary">
            {overlay.detail}
          </p>
        ) : null}
      </div>
    </div>
  )
}
